import { createElement } from 'react';
import { TConfirmedOrdersRow } from 'redux/reducers/orders/types';
import { ERefundStatus, ORDERS_STATUS } from 'redux/reducers/orders/constants';
import {
	ETableStructureAlign,
	ETableStructureType,
	TTableStructure,
} from 'services/constants/tableStructureTypes';
import { DATES, DATES_ID } from 'services/constants/dates';
import {
	ESalesTypes,
	OrderSchemeCode,
	OrderSchemeName,
} from 'services/constants/representationSchemeType';
import { AmountSpeedDialMemo } from './AmountSpeedDial';
import { COLORS } from 'services/constants/colors';

const DATE_FILTER: [string, string][] = [
	[DATES_ID.TODAY, DATES.TODAY],
	[DATES_ID.YESTERDAY, DATES.YESTERDAY],
	[DATES_ID.LAST_7_DAYS, DATES.LAST_7_DAYS],
	[DATES_ID.LAST_30_DAYS, DATES.LAST_30_DAYS],
	[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
	[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
	[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
	[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
	[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
	[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
	[DATES_ID.CUSTOM_RANGE_PAST, DATES.CUSTOM_RANGE],
];

const SCHEMES_FILTER: [string, string][] = [
	[OrderSchemeCode.IOSS_NON_UNION, OrderSchemeName[OrderSchemeCode.IOSS_NON_UNION]],
	[OrderSchemeCode.IOSS_UNION, OrderSchemeName[OrderSchemeCode.IOSS_UNION]],
	[OrderSchemeCode.IOSS_IMPORT, OrderSchemeName[OrderSchemeCode.IOSS_IMPORT]],
	[OrderSchemeCode.FC, OrderSchemeName['FC-REP']],
	[OrderSchemeCode.B2B_INTRA_COMMUNAL, OrderSchemeName[OrderSchemeCode.B2B_INTRA_COMMUNAL]],
	[OrderSchemeCode.B2B_DOMESTIC, OrderSchemeName[OrderSchemeCode.B2B_DOMESTIC]],
	[OrderSchemeCode.B2C_DOMESTIC, OrderSchemeName[OrderSchemeCode.B2C_DOMESTIC]],
	[OrderSchemeCode.GIFTCARD_SCHEME, OrderSchemeName.GIFTCARD_SCHEME],
	[OrderSchemeCode.NON_EU_IMPORT, OrderSchemeName.NON_EU_IMPORT],
	[OrderSchemeCode.NON_EU_DOMESTIC, OrderSchemeName.NON_EU_DOMESTIC],
	[OrderSchemeCode.EI_FACILITATION, OrderSchemeName.EI_FACILITATION],
];

const SCHEMES_COLORS: [string, string][] = SCHEMES_FILTER.map(([key]) => [key, COLORS.SECONDARY]);

const STATUS_FILTER: [string, string][] = [
	[ORDERS_STATUS.PAID, 'paid'],
	[ORDERS_STATUS.AWAITING_PAYMENT, 'awaiting payment'],
];

const STATUS_COLORS: [string, string][] = [
	[ORDERS_STATUS.PAID, COLORS.SUCCESS],
	[ORDERS_STATUS.AWAITING_PAYMENT, COLORS.WARNING],
];

const SALES_TYPE_FILTER: [string, string][] = [
	[ESalesTypes.B2C, ESalesTypes.B2C],
	[ESalesTypes.B2B, ESalesTypes.B2B],
];

const SALES_TYPE_COLORS: [string, string][] = [
	[ESalesTypes.B2C, COLORS.SECONDARY],
	[ESalesTypes.B2B, COLORS.WARNING],
];

const ORDER_TAGS_COLORS: [string, string][] = [
	['returns', COLORS.RETURNED],
	['discounts', COLORS.DISCOUNT],
	['corrections', COLORS.CORRECTION],
];

const REFUND_STATUS_COLORS: [string, string][] = [
	[ERefundStatus.REFUNDED, COLORS.SUCCESS],
	[ERefundStatus.PARTIAL_REFUND, COLORS.WARNING],
	[ERefundStatus.NOT_REFUNDED, COLORS.SECONDARY],
];

export const confirmedOrdersStructure: TTableStructure<
	keyof TConfirmedOrdersRow,
	TConfirmedOrdersRow
>[] = [
	{
		name: 'EAS Order Id',
		selector: 'id',
		show: true,
		sortable: true,
		filterable: false,
		searchable: true,
		searchInfoText:
			"The search will be conducted based on a full match with the EAS order ID or a partial match with the store's order number.",
		colSpan: 1,
		type: ETableStructureType.LINK,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Date',
		selector: 'timestamp',
		show: true,
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
		colSpan: 1,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.CENTER,
	},
	{
		name: 'Store Order #',
		selector: 'externalOrderId',
		show: true,
		sortable: true,
		filterable: false,
		searchable: true,
		colSpan: 1,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Delivery Country',
		selector: 'deliveryCountry',
		show: true,
		sortable: true,
		filterable: true,
		colSpan: 1,
		type: ETableStructureType.COUNTRY_CODE_SUPPORTED,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Sales type',
		selector: 'salesType',
		show: true,
		sortable: true,
		filterable: true,
		filter: SALES_TYPE_FILTER,
		colors: SALES_TYPE_COLORS,
		colSpan: 1,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'VAT Schemes',
		selector: 'schemes',
		show: true,
		sortable: true,
		filterable: true,
		filter: SCHEMES_FILTER,
		colors: SCHEMES_COLORS,
		colSpan: 1,
		type: ETableStructureType.STATUS_ARRAY,
		align: ETableStructureAlign.LEFT,
		value: (item: TConfirmedOrdersRow) =>
			(item?.schemes ? item.schemes.split(',') : []) as unknown as string,
	},
	{
		name: 'Total',
		selector: 'totalOrderValue',
		show: true,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.AMOUNT,
		currency: (item: TConfirmedOrdersRow) => item?.paymentCurrency ?? 'EUR',
		align: ETableStructureAlign.RIGHT,
		breakdown: (item: TConfirmedOrdersRow) =>
			createElement(AmountSpeedDialMemo, {
				merchandiseCost: item?.totalMerchCost ?? 0,
				deliveryCost: item?.deliveryCost ?? 0,
				taxes: item?.totalVat ?? 0,
				customDuties: item?.totalCustomDuties ?? 0,
				easFee: item?.easFeesDue ?? 0,
				customsProceduresFee: item?.totalDpoFee ?? 0,
				orderCurrency: item?.paymentCurrency ?? '',
				thresholdCurrency: item?.thresholdCurrency ?? '',
				intrinsicThresholdExchangeRate: item?.intrinsicThresholdExchangeRate ?? 0,
				importThreshold: `${item?.importThreshold ?? ''}`,
			}),
	},
	{
		name: 'Status',
		selector: 'status',
		show: true,
		sortable: true,
		filterable: true,
		filter: STATUS_FILTER,
		colors: STATUS_COLORS,
		colSpan: 1,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Refund Status',
		selector: 'refundStatus',
		show: true,
		sortable: true,
		filterable: true,
		filter: [
			[ERefundStatus.REFUNDED, 'refunded'],
			[ERefundStatus.PARTIAL_REFUND, 'partial refund'],
			[ERefundStatus.NOT_REFUNDED, 'not refunded'],
		],
		colors: REFUND_STATUS_COLORS,
		colSpan: 1,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Has Returns',
		selector: 'hasReturns',
		show: true,
		noName: true,
		sortable: false,
		filterable: true,
		filter: [
			['true', 'Returns'],
			['false', 'No Returns'],
		],
		colSpan: 1,
		type: ETableStructureType.STATUS_ARRAY,
		align: ETableStructureAlign.LEFT,
		colors: ORDER_TAGS_COLORS,
		value: (item: TConfirmedOrdersRow) => {
			const tags: string[] = [];
			if (item.hasReturns) tags.push('returns');
			if (item.hasDiscounts) tags.push('discounts');
			if (item.hasCorrections) tags.push('corrections');
			return tags as unknown as string;
		},
	},
	{
		name: 'Has Discounts',
		selector: 'hasDiscounts',
		show: false,
		noName: true,
		sortable: false,
		filterable: true,
		filter: [
			['true', 'Discounts'],
			['false', 'No Discounts'],
		],
		colSpan: 1,
		type: ETableStructureType.BOOLEAN,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Has Corrections',
		selector: 'hasCorrections',
		show: false,
		noName: true,
		sortable: false,
		filterable: true,
		filter: [
			['true', 'Corrections'],
			['false', 'No Corrections'],
		],
		colSpan: 1,
		type: ETableStructureType.BOOLEAN,
		align: ETableStructureAlign.LEFT,
	},
];
